
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { MenuBlockConfiguration } from '@/types/block-configurations';

export function MenuBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as MenuBlockConfiguration;
  const isHorizontal = config.layout !== 'vertical';
  // Mirrors MenuBlockRenderer.cs: null/undefined falls back to "|", empty string means none
  const separator = config.separator ?? '|';

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-menu cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        paddingTop: `${config.padding?.top ?? 10}px`,
        paddingRight: `${config.padding?.right ?? 10}px`,
        paddingBottom: `${config.padding?.bottom ?? 10}px`,
        paddingLeft: `${config.padding?.left ?? 10}px`,
        textAlign: config.alignment?.horizontal || 'center',
      }}
    >
      {config.items && config.items.length > 0 ? (
        <nav
          className={isHorizontal ? 'flex flex-wrap items-center' : 'flex flex-col'}
          style={{
            gap: `${config.itemSpacing ?? 15}px`,
            justifyContent: config.alignment?.horizontal === 'left' ? 'flex-start' : config.alignment?.horizontal === 'right' ? 'flex-end' : 'center',
          }}
        >
          {config.items.map((item, i) => (
            <span key={i} className="flex items-center">
              <a
                href={item.href}
                title={item.title}
                onClick={(e) => e.preventDefault()}
                style={{
                  fontSize: `${config.typography?.fontSize ?? 14}px`,
                  fontWeight: config.typography?.fontWeight || 'normal',
                  color: config.typography?.color || '#333333',
                  fontFamily: config.typography?.fontFamily || 'Arial',
                  // Backend renders menu links without underline
                  textDecoration: 'none',
                }}
              >
                {item.text}
              </a>
              {isHorizontal && separator && i < config.items.length - 1 && (
                <span
                  style={{
                    color: config.typography?.color || '#999999',
                    marginLeft: `${config.itemSpacing ?? 15}px`,
                  }}
                >
                  {separator}
                </span>
              )}
            </span>
          ))}
        </nav>
      ) : (
        <div className="flex justify-center gap-4">
          {['Home', 'About', 'Contact'].map((label) => (
            <span
              key={label}
              className="text-sm text-muted-foreground underline"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
